import { externalUrls } from "@/constants/url";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { transformToPascal } from "@/utils/string";
import {
  Icon,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeadRow,
  TableHeader,
  TableRow,
  Thumbnail,
  ThumbnailFallback,
  ThumbnailImage,
  Typography,
} from "@backpac/monica";
import { type ReactNode, Suspense } from "react";
import { formatMinutes } from "../../../utils/time";
import { useMusicControls } from "../context/music-controls-context";
import { useMusics } from "../hooks/use-musics";
import { FavoriteMusicButton } from "./favorite-music-button";
import { IndicatorMusicStatus } from "./indicator-music-status";
import { MusicMoodFilter } from "./music-mood-filter";
import { StaticWaveForm } from "./static-wave-form";
import { UseMusicButton } from "./use-music-button";

// Define Music type based on the data structure
interface Music {
  assetId: string;
  isrc: string;
  title: {
    en: string;
  };
  moods: string[];
  duration: number;
  isFavorite: boolean;
  ytVideoId: string | undefined;
}

interface MusicTableProps {
  className?: string;
  favorite?: boolean;
  caption?: ReactNode;
}

interface MusicTableDataProps {
  musics: Music[];
}

// MusicTableProvider component for data fetching
interface MusicTableProviderProps {
  children: (props: MusicTableDataProps) => ReactNode;
  favorite?: boolean;
}

export function MusicTableProvider({ children, favorite }: MusicTableProviderProps) {
  const { data: musics } = useMusics({ favorite });

  return <>{children({ musics })}</>;
}

// Main MusicTable component
export function MusicTable(props: MusicTableProps) {
  const isMobile = useIsMobile();

  return (
    <MusicTableProvider favorite={props.favorite}>
      {({ musics }) =>
        isMobile ? (
          <MusicTable.Mobile musics={musics} caption={<MusicMoodFilter />} {...props} />
        ) : (
          <MusicTable.Desktop musics={musics} caption={<MusicMoodFilter />} {...props} />
        )
      }
    </MusicTableProvider>
  );
}

// Component for empty favorites state
function EmptyFavorites() {
  const isMobile = useIsMobile();

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={isMobile ? 3 : 6} className="text-center">
        <div className="space-y-3 py-12">
          <div className="text-colorRed">
            <Icon name="heart-fill" bgColor="colorRedLight" iconSize="24px" />
          </div>
          <div className="space-y-2">
            <Typography size="md" color="textPrimary" weight={600}>
              No favorites yet
            </Typography>
            <Typography size="xs" color="textMuted">
              Tracks that you favorite will appear here
            </Typography>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

MusicTable.Desktop = function MusicTableDesktop({ className, musics, caption }: MusicTableProps & MusicTableDataProps) {
  return (
    <div className={cn("space-y-[40px]", className)}>
      <Table className="caption-top" border={false}>
        <TableCaption className="pl-6 pb-3 text-left">{caption}</TableCaption>
        <TableHeader>
          <TableHeadRow>
            <TableHead className="pl-6">Track</TableHead>
            <TableHead>Mood</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Soundbar</TableHead>
            <TableHead>Favorite</TableHead>
            <TableHead className="pr-6 text-center">Use</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {musics.length === 0 ? (
            <EmptyFavorites />
          ) : (
            musics.map((music) => (
              <MusicTableRow assetId={music.assetId} key={music.assetId}>
                <TableCell className="pl-6">
                  <div className="flex items-center gap-3">
                    <IndicatorMusicStatus assetId={music.assetId}>
                      <Thumbnail variant="square" className="size-14">
                        <ThumbnailImage
                          src={`${externalUrls.MUSIC_RESOURCE_BASE_URL}/${music.isrc}.avif`}
                          alt={music.title.en}
                        />
                        <ThumbnailFallback> </ThumbnailFallback>
                      </Thumbnail>
                    </IndicatorMusicStatus>
                    <Typography size="xs" weight={400} color="textPrimary">
                      {music.title.en}
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>
                  <Typography size="xs" weight={400} color="textMuted" className="max-w-[180px]">
                    {music.moods.map(transformToPascal).join(", ")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography size="xs" weight={400} color="textMuted">
                    {formatMinutes(music.duration)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Suspense fallback={<div className="w-[320px] h-[48px] bg-gray-100 rounded-md" />}>
                    <StaticWaveForm isrc={music.isrc} />
                  </Suspense>
                </TableCell>
                <TableCell>
                  <FavoriteMusicButton assetId={music.assetId} isFavorite={music.isFavorite} />
                </TableCell>
                <TableCell className="pr-6">
                  <UseMusicButton
                    isrc={music.isrc}
                    title={music.title.en}
                    youtubeVideoId={music.ytVideoId ?? ""}
                    thumbnailImageUrl={`${externalUrls.MUSIC_RESOURCE_BASE_URL}/${music.isrc}.avif`}
                  />
                </TableCell>
              </MusicTableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

MusicTable.Mobile = function MusicTableMobile({ className, musics, caption }: MusicTableProps & MusicTableDataProps) {
  return (
    <div className={cn("space-y-[40px]", className)}>
      <Table className="caption-top" border={false}>
        <TableCaption className="pl-4 pb-8 text-left">{caption}</TableCaption>
        <TableHeader>
          <TableHeadRow>
            <TableHead className="pl-4">Track</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {musics.length === 0 ? (
            <EmptyFavorites />
          ) : (
            musics.map((music) => (
              <TableRow key={music.assetId}>
                <TableCell className="pl-4">
                  <div className="flex items-center gap-4">
                    <PlayMusicArea assetId={music.assetId}>
                      <IndicatorMusicStatus assetId={music.assetId}>
                        <Thumbnail variant="square" className="size-14">
                          <ThumbnailImage
                            src={`${externalUrls.MUSIC_RESOURCE_BASE_URL}/${music.isrc}.avif`}
                            alt={music.title.en}
                          />
                          <ThumbnailFallback>{music.title?.en?.slice(0, 2)}</ThumbnailFallback>
                        </Thumbnail>
                      </IndicatorMusicStatus>
                    </PlayMusicArea>
                    <PlayMusicArea assetId={music.assetId} className="flex flex-col gap-1 flex-1 text-left">
                      <Typography size="xs" weight={400} color="textPrimary">
                        {music.title.en}
                      </Typography>
                      <Typography size="2xs" weight={400} color="textMuted">
                        {music.moods.map(transformToPascal).join(", ")}
                      </Typography>
                      <Typography size="2xs" weight={400} color="textMuted">
                        {formatMinutes(music.duration)}
                      </Typography>
                    </PlayMusicArea>
                    <div className="flex items-center gap-2">
                      <FavoriteMusicButton assetId={music.assetId} isFavorite={music.isFavorite} />
                      <UseMusicButton
                        isrc={music.isrc}
                        title={music.title.en}
                        youtubeVideoId={music.ytVideoId ?? ""}
                        thumbnailImageUrl={`${externalUrls.MUSIC_RESOURCE_BASE_URL}/${music.isrc}.avif`}
                      />
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

function MusicTableRow({ assetId, children }: { assetId: string; children: React.ReactNode }) {
  const { status, currentMusicAssetId, controls } = useMusicControls();

  return (
    <TableRow
      className="cursor-pointer"
      onClick={() => {
        if (status === "playing" && currentMusicAssetId === assetId) {
          controls.pause();
        } else {
          controls.play(assetId);
        }
      }}
    >
      {children}
    </TableRow>
  );
}

function PlayMusicArea({
  assetId,
  children,
  className,
}: { assetId: string; children: React.ReactNode; className?: string }) {
  const { status, currentMusicAssetId, controls } = useMusicControls();

  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        if (status === "playing" && currentMusicAssetId === assetId) {
          controls.pause();
        } else {
          controls.play(assetId);
        }
      }}
    >
      {children}
    </button>
  );
}
